import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import type { AnyFieldApi } from "@tanstack/react-form"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

type FieldRenderFn = (fieldApi: AnyFieldApi) => React.ReactNode

type FieldProps = {
  name: string
  validators?: unknown
  children: FieldRenderFn
}

type FormInstanceLike = {
  Field: React.ComponentType<FieldProps>
}

const FormInstanceContext = React.createContext<FormInstanceLike | null>(null)

const Form = ({ form, children }: { form: unknown; children: React.ReactNode }) => (
  <FormInstanceContext.Provider value={form as FormInstanceLike}>
    {children}
  </FormInstanceContext.Provider>
)

type FormFieldContextValue = {
  name: string
  errorText?: string
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null)

type InputValue = string | number | readonly string[] | undefined

type BridgeField = {
  name: string
  value: InputValue
  onChange: (value: unknown) => void
  onBlur: () => void
}

type FormFieldComponentProps = {
  name: string
  validators?: unknown
  render: (props: { field: BridgeField }) => React.ReactNode
}

const extractErrorMessage = (error: unknown): string | undefined => {
  if (error == null) {
    return undefined
  }
  if (typeof error === "string") {
    return error
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message
    return message == null ? undefined : String(message)
  }
  return String(error)
}

const FormField = ({ name, validators, render }: FormFieldComponentProps) => {
  const form = React.useContext(FormInstanceContext)

  if (!form) {
    throw new Error("FormField must be used within a Form")
  }

  return (
    <form.Field name={name} validators={validators}>
      {(fieldApi: AnyFieldApi) => {
        const errors = fieldApi.state.meta.errors ?? []
        const errorText = extractErrorMessage(errors[0])

        const field: BridgeField = {
          name,
          value: fieldApi.state.value,
          onChange: (raw) => {
            if (
              raw != null &&
              typeof raw === "object" &&
              "target" in raw &&
              raw.target != null
            ) {
              const target = raw.target as {
                type?: string
                value?: unknown
                checked?: unknown
              }
              fieldApi.handleChange(
                target.type === "checkbox" ? target.checked : target.value
              )
            } else {
              fieldApi.handleChange(raw)
            }
          },
          onBlur: fieldApi.handleBlur,
        }

        return (
          <FormFieldContext.Provider value={{ name, errorText }}>
            {render({ field })}
          </FormFieldContext.Provider>
        )
      }}
    </form.Field>
  )
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue | null>(null)

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>")
  }

  const { id } = itemContext
  const { name, errorText } = fieldContext

  return {
    id,
    name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    error: errorText ? { message: errorText } : undefined,
    invalid: Boolean(errorText),
  }
}

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  )
})
FormItem.displayName = "FormItem"

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField()

  return (
    <Label
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  )
})
FormLabel.displayName = "FormLabel"

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
})
FormControl.displayName = "FormControl"

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField()

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
FormDescription.displayName = "FormDescription"

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message ?? "") : children

  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  )
})
FormMessage.displayName = "FormMessage"

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
}
